var _Search;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useRef, useState } from 'react';
import { textFieldClasses } from '@mui/material/TextField';
import { inputBaseClasses } from '@mui/material/InputBase';
import { arrayOf, bool, func, object, oneOfType, string, node } from 'prop-types';
import clsx from 'clsx';
import { Search } from 'mosaic-react-icons';
import { useTheme, alpha, styled } from '@mui/material/styles';
import { useClickOutside } from '../hooks';
import { mergeClassNameIntoProps } from '../internals/utils/classNameHelpers';
import SovosIconButton from '../sovos-icon-button';
import SovosTextField from '../sovos-text-field';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const outlineStyle = function (color) {
  let width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  return {
    outline: "solid ".concat(width, " ").concat(color),
    outlineOffset: "-".concat(width)
  };
};
const SearchForm = styled('form')(_ref => {
  let {
    theme: {
      palette,
      spacing,
      transitions
    }
  } = _ref;
  const width = spacing(40);
  const outlineWidth = spacing(0.25); // 2px

  return {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    height: 'calc(100% + 2px)',
    // overlap the toolbar horizontal borders
    justifyContent: 'flex-end',
    maxWidth: width,
    transition: "width ".concat(transitions.duration.standard, "ms, outline ").concat(transitions.duration.short, "ms"),
    width: spacing(5),
    zIndex: 1000,
    ...outlineStyle(alpha(palette.text.secondary, 0)),
    '&.sovosToolbarSearch--expanded': {
      width,
      '&:hover': outlineStyle(alpha(palette.text.secondary, 0.5), outlineWidth)
    },
    '&.sovosToolbarSearch--active': {
      ...outlineStyle(palette.primary.main, outlineWidth),
      '&:hover': outlineStyle(palette.primary.main, outlineWidth)
    }
  };
});
const StyledTextField = styled(SovosTextField)(_ref2 => {
  let {
    theme: {
      spacing,
      transitions
    }
  } = _ref2;
  return {
    ["&.".concat(textFieldClasses.root)]: {
      flex: '1 0 auto',
      height: '100%'
    },
    ["& .".concat(inputBaseClasses.root)]: {
      height: '100%',
      paddingInlineEnd: spacing(),
      paddingInlineStart: spacing(2),
      overflow: 'hidden',
      transition: "width ".concat(transitions.duration.standard, "ms"),
      width: 0,
      '&[data-expanded="true"]': {
        width: '100%'
      }
    }
  };
});

/**
 * **Note**: must be a child of `SovosToolbar`
 */
const SovosToolbarSearch = _ref3 => {
  let {
    classes,
    className,
    'data-testid': dataTestId,
    defaultValue = '',
    disabled = false,
    IconButtonProps = {},
    onSearch,
    placeholder,
    searchTooltipText,
    sx
  } = _ref3;
  const {
    palette
  } = useTheme();
  const [value, setValue] = useState(defaultValue);
  const [isActive, setIsActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    t
  } = useMosaicTranslation();
  const color = isActive ? palette.text.primary : palette.text.secondary;
  const rootRef = useRef(null);
  const handleSubmit = useCallback(e => {
    e.preventDefault();

    // Button click open the input if in-active
    if (!isExpanded && !isActive) {
      setIsExpanded(true);
      setIsActive(true);
      return;
    }

    // Fire onSearch if active
    if (isExpanded && onSearch) {
      onSearch(value);
    }
  }, [isActive, isExpanded, onSearch, value]);
  const clearForm = function () {
    let {
      clear = false
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    setIsActive(false);
    if (isExpanded) setIsExpanded(false);
    if (clear) {
      setIsExpanded(false);
      if (value) {
        setValue('');
      }
    }
  };
  useClickOutside(rootRef, () => {
    setIsActive(false);
    if (value === '') setIsExpanded(false);
  });
  const handleFormKeyDown = event => {
    if (event.key === 'Escape') {
      clearForm({
        clear: true
      });
    }
  };
  const handleBtnKeyDown = event => {
    if (event.key === 'Tab') {
      clearForm();
    }
  };
  if (!IconButtonProps.tooltipText) {
    // eslint-disable-next-line no-param-reassign
    IconButtonProps.tooltipText = searchTooltipText || t('search');
  }
  const renderSearchField = () => isExpanded || value ? /*#__PURE__*/React.createElement(StyledTextField, {
    autoFocus: !!isExpanded,
    className: clsx('sovosToolbarSearch__input', classes === null || classes === void 0 ? void 0 : classes.text),
    "data-testid": "sovosToolbarSearch__searchInput",
    InputProps: {
      'data-expanded': isExpanded || value ? 'true' : 'false',
      disableUnderline: true
    },
    name: "toolbar-search-text",
    onChange: e => setValue(e.target.value),
    onFocus: () => setIsActive(true),
    placeholder: placeholder || t('search'),
    value: value,
    variant: "standard"
  }) : null;
  return /*#__PURE__*/React.createElement(SearchForm, {
    className: clsx('sovosToolbarSearch', className, classes === null || classes === void 0 ? void 0 : classes.root, {
      'sovosToolbarSearch--active': isActive,
      [classes === null || classes === void 0 ? void 0 : classes.active]: isActive && classes,
      'sovosToolbarSearch--expanded': isExpanded || value,
      [classes === null || classes === void 0 ? void 0 : classes.expanded]: (isExpanded || value) && classes
    }),
    "data-testid": dataTestId,
    onKeyDown: handleFormKeyDown,
    onSubmit: e => handleSubmit(e),
    ref: rootRef,
    sx: sx
  }, renderSearchField(), /*#__PURE__*/React.createElement(SovosIconButton, _extends({
    color: color,
    "data-testid": "sovosToolbarSearch__searchButton",
    disabled: disabled,
    onClick: handleSubmit,
    onKeyDown: handleBtnKeyDown,
    type: isExpanded ? 'submit' : 'button'
  }, mergeClassNameIntoProps(IconButtonProps, clsx('sovosToolbarSearch__searchButton', {
    'sovosToolbarSearch__searchButton--expanded': isExpanded,
    [classes === null || classes === void 0 ? void 0 : classes.iconExpanded]: isActive && classes
  }))), _Search || (_Search = /*#__PURE__*/React.createElement(Search, null))));
};
SovosToolbarSearch.propTypes = {
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * Initial value of the search input
   */
  defaultValue: string,
  /**
   * If `true`, the button is disabled
   */
  disabled: bool,
  /**
   * Props passed to the Icon button component
   */
  IconButtonProps: object,
  /**
   * Callback fired when search icon button has been clicked or on Enter,
   * after search input has been opened `function(value: string) => void`
   */
  onSearch: func,
  /**
   * Text label for the search input placeholder attribute
   */
  placeholder: string,
  /**
   * Text label for the icon button `aria-label` attribute and tooltip
   */
  searchTooltipText: node,
  /**
   * The System prop that allows defining system overrides as well as
   * additional CSS styles.
   */
  sx: oneOfType([arrayOf(oneOfType([func, object, bool])), func, object])
};
export default SovosToolbarSearch;